<i18n>
{
  "fr": {
    "configuratorAddToCartModal": {
      "title": {
        "isLoading": "Ajout au panier en cours…",
        "success": "Votre produit a bien été ajouté"
      },
      "action": {
        "continue": "Continuer mes achats",
        "goToCart": "Accéder au panier",
        "continueCustomOrder": "Poursuivre en BO"
      }
    }
  }
}
</i18n>

<template>
  <MountingPortal
    :name="`add-to-cart-modal--${id}`"
    append
    mount-to="#portal-container--footer"
  >
    <BaseModal
      ref="modal"
      :is-active="isModalActive"
      hide-close-button
      :close-on-backdrop-click="false"
      class="configurator-add-to-cart-modal"
      full-screen-until-xs
      @close="onClose"
    >
      <template #header></template>

      <template #default>
        <div class="configurator-add-to-cart-modal__header">
          <div class="configurator-add-to-cart-modal__header-title">
            <template v-if="isLoading">
              {{ $t('configuratorAddToCartModal.title.isLoading') }}
            </template>
            <template v-else>
              {{ $t('configuratorAddToCartModal.title.success') }}
            </template>
          </div>
        </div>

        <ConfiguratorCustomizationSummary
          :color="activeColor.name"
          :customizations="activeZoneCustomizations"
          :price="price"
          :price-without-vat="priceWithoutVAT"
          :product="activeProduct.name"
          :quantity="activeQuantity.name"
          :size="activeSize.name"
          :thumbnails="previewThumbnails"
          :unit-price="pricePerUnit"
          :unit-price-without-vat="priceWithoutVATPerUnit"
          :express-mode="isExpressDeliveryEnabled"
          class="configurator-add-to-cart-modal__summary"
        />

        <ConfiguratorAddToCartUi
          v-if="isLoading"
          ref="ui"
          @success="onSuccess"
          @error="onError"
        />
        <template v-else>
          <BtnToolbar vertical-until-xs :full-width-equal="isFullwidthEqual">
            <BaseBtn
              class="configurator-add-to-cart-modal__btn"
              block
              border
              @click="routeTo(blankConfiguratorUrl)"
            >
              {{ $t('configuratorAddToCartModal.action.continue') }}
            </BaseBtn>
            <BaseBtn
              v-if="cartUrl"
              class="configurator-add-to-cart-modal__btn configurator-add-to-cart-modal__btn--next"
              secondary
              block
              @click="routeTo(cartUrl)"
            >
              {{ $t('configuratorAddToCartModal.action.goToCart') }}
            </BaseBtn>
          </BtnToolbar>

          <BtnToolbar vertical-until-xs :full-width-equal="isFullwidthEqual">
            <BaseBtn
              v-if="createCustomOrderUrl"
              class="configurator-add-to-cart-modal__btn configurator-add-to-cart-modal__btn--next"
              :href="createCustomOrderUrl"
              secondary
              block
            >
              {{ $t('configuratorAddToCartModal.action.continueCustomOrder') }}
            </BaseBtn>
          </BtnToolbar>
        </template>
      </template>

      <template #footer>
        <ConfiguratorAddToCartFooter :products="similarProducts" />
      </template>
    </BaseModal>
  </MountingPortal>
</template>

<script>
import BaseModal from '../BaseModal';
import { randomStr } from '../../utils/String';
import ConfiguratorAddToCartFooter from './ConfiguratorAddToCartFooter';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
  mapState as mapConfiguratorState,
} from '../../store/modules/configurator';
import ConfiguratorCustomizationSummary from './ConfiguratorCustomizationSummary';
import ConfiguratorAddToCartUi from './ConfiguratorAddToCartUi';
import BtnToolbar from '../BtnToolbar';
import BaseBtn from '../BaseBtn';
import { getCompatibleProductsFromStore } from '../../utils/CrossSellUtils';
import { mapState as mapMiscState } from '../../store/modules/configurator/misc';
import { Breakpoint } from '../../utils/breakpoints';
import { ThemeType } from '../../api/static/ThemeType';

export default {
  name: 'ConfiguratorAddToCartModal',
  emits: ['show-cross-sell'],
  components: {
    BaseBtn,
    BtnToolbar,
    ConfiguratorAddToCartUi,
    ConfiguratorCustomizationSummary,
    ConfiguratorAddToCartFooter,
    BaseModal,
  },
  props: {
    isModalActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      id: randomStr(),
      similarProducts: [],
      isLoading: true,
      previewThumbnails: {
        center: undefined,
        left: undefined,
        right: undefined,
        back: undefined,
      },
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeProductVariant: ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT,
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activeColor: ConfiguratorGetters.GET_ACTIVE_COLOR,
      activeProduct: ConfiguratorGetters.GET_ACTIVE_PRODUCT,
      activeQuantity: ConfiguratorGetters.GET_ACTIVE_QUANTITY,
      activeConfiguration: ConfiguratorGetters.GET_ACTIVE_CONFIGURATION,
      activeZoneCustomizations:
        ConfiguratorGetters.GET_ACTIVE_ZONE_CUSTOMIZATIONS,
      pricePerUnit: ConfiguratorGetters.GET_PRICE_PER_UNIT,
      priceWithoutVATPerUnit:
        ConfiguratorGetters.GET_PRICE_WITHOUT_VAT_PER_UNIT,
      price: ConfiguratorGetters.GET_PRICE,
      priceWithoutVAT: ConfiguratorGetters.GET_PRICE_WITHOUT_VAT,
    }),
    ...mapConfiguratorState({
      themeType: 'themeType',
    }),
    ...mapMiscState([
      'createCustomOrderUrl',
      'cartUrl',
      'blankConfiguratorUrl',
    ]),
    isFullwidthEqual() {
      return this.$matchMedia[Breakpoint.XS];
    },
    isExpressDeliveryEnabled() {
      return this.$store.state.configurator.isExpressDeliveryEnabled;
    },
  },
  watch: {
    isModalActive: {
      handler(isModalActive) {
        if (isModalActive) {
          this.$nextTick()
            .then(() => {
              return this.$refs.ui.getPreviewThumbnails();
            })
            .then(thumbnails => {
              this.revokeThumbnails();
              this.previewThumbnails = thumbnails;
            });
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.revokeThumbnails();
  },
  methods: {
    revokeThumbnails() {
      Object.values(this.previewThumbnails).forEach(url => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    },
    onClose() {
      this.$emit('close');
    },
    onSuccess() {
      document.dispatchEvent(
        new CustomEvent('checkoutChange', { detail: true })
      );
      setTimeout(() => {
        this.isLoading = false;

        this.$nextTick(() => {
          const bodyEl = this.$refs.modal?.$refs?.body;
          if (bodyEl) {
            bodyEl.scrollTo({
              top: bodyEl.scrollHeight,
              behavior: 'smooth',
            });
          }
        });
      }, 500);
    },
    onError() {
      this.$emit('close');
    },
    routeTo(destination) {
      // Calculate compatible products using shared util (resolves getters from store)
      const compatibleProducts = getCompatibleProductsFromStore(this.$store, {
        productId: this.activeProduct.id,
        sizeId: this.activeSize.id,
        quantityId: this.activeQuantity.id,
        themeType: this.themeType,
      });

      if (
        this.themeType === ThemeType.GENERIC &&
        compatibleProducts.length > 0
      ) {
        this.$emit('show-cross-sell');
      } else {
        window.location.href = destination;
      }
    },
  },
};
</script>

<style lang="scss">
.configurator-add-to-cart-modal {
  &__summary {
    margin-bottom: 1em;
  }

  &__header-title {
    background: linear-gradient(
      120deg,
      $color-blue-cerulean-blue 44.9%,
      $color-arapawa 96.27%
    );
    line-height: 1.25;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    font-family: $font-big-shoulders;
    font-size: 1.5625rem;
    font-weight: 900;
    max-width: 10.625rem;
    margin: 0 auto;
    text-align: center;
  }

  &__btn {
    font-size: 1rem;
    font-weight: 700;
    min-height: 2.75rem;

    &--next {
      margin-top: 0.625rem;

      &::after {
        @extend %icon;
        @extend %icon--arrow-right1;

        margin-left: 0.5em;
      }

      @include breakpoint($breakpoint-l) {
        margin-top: 1.25rem;
      }
    }
  }

  &.base-modal--full-screen-until-xs {
    .base-modal {
      &__container {
        background-color: $color-gray-wild-sand;

        @include breakpoint($breakpoint-xs) {
          width: 100%;
          max-width: $breakpoint-xs;
        }
      }

      &__header {
        background-color: transparent;
        border-bottom: none;
      }

      &__body {
        background-color: transparent;
      }

      &__body-container {
        margin: 1em;
        padding: 0;
      }
    }
  }
}
</style>
